import { useCallback, useState } from 'react';
import type { CalendarColumnGroup } from '@frontend/calendar-grid';
import {
  useColumnEntityReorderStore,
  type ReorderConfirmHandlerResponse,
} from '../../components/calendar-view/providers';
import type { CalendarEntityType } from '../../stores/calendar.store';

/**
 * Hook to bridge CalendarGrid's onColumnReorder callback to the ColumnEntityReorderStore.
 * Enables the Save Layout / Cancel UX when columns are reordered via drag-and-drop.
 *
 * Maintains a local pending order per group so the CalendarGrid reflects the new
 * column order immediately after a drop — before the user clicks Save Layout.
 * Without this, the widget's temporary localColumnOrderByGroup is cleared after
 * onColumnReorder fires and the columns snap back to the original prop-driven order.
 *
 * @param calendarEntityType - 'provider' or 'operatory'
 * @param baseColumnGroups - the original columnGroups from the data hook
 * @returns onColumnReorder callback and merged columnGroups to pass to CalendarGrid
 */
export const useCalendarGridColumnReorder = (
  calendarEntityType: CalendarEntityType,
  baseColumnGroups: CalendarColumnGroup[]
) => {
  const { addToColumnReorderHandlers, removeColumnReorderHandler } = useColumnEntityReorderStore([
    'addToColumnReorderHandlers',
    'removeColumnReorderHandler',
  ]);

  // Pending local order by groupId — holds the new column order until Save or Cancel
  const [pendingOrderByGroup, setPendingOrderByGroup] = useState<Record<string, string[]>>({});

  const handleColumnReorder = useCallback(
    (groupId: string, newColumnIds: string[]) => {
      const locationId = groupId;

      // Store the new order locally so CalendarGrid reflects it immediately
      setPendingOrderByGroup((prev) => ({ ...prev, [groupId]: newColumnIds }));

      // Convert array to orderIdMap: { [columnId]: orderIndex }
      const newOrderIdMap = newColumnIds.reduce<Record<string, number>>((acc, id, index) => {
        acc[id] = index;
        return acc;
      }, {});

      addToColumnReorderHandlers(locationId, {
        confirm: () => {
          // On Save: clear pending state for this group (preference now owns the order)
          setPendingOrderByGroup((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
          return [calendarEntityType, locationId, newOrderIdMap] as ReorderConfirmHandlerResponse;
        },
        cancel: () => {
          // On Cancel: clear pending state to revert to the preference-driven order
          setPendingOrderByGroup((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
          removeColumnReorderHandler(locationId);
        },
      });
    },
    [calendarEntityType, addToColumnReorderHandlers, removeColumnReorderHandler]
  );

  // Merge pending order into base columnGroups — pending overrides prop-driven order
  const columnGroups = baseColumnGroups.map((group) => {
    const pendingIds = pendingOrderByGroup[group.id];
    if (!pendingIds) return group;
    return { ...group, columnIds: pendingIds };
  });

  return { onColumnReorder: handleColumnReorder, columnGroups };
};
